import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { apiProducts } from '../../api/apiProducts.api';
import DotSpinner from '../../components/shared/DotSpinner';
import { apiPayment } from '../../api/apiPayment.api';
import { toast } from 'react-toastify';
import { openLink } from '../../utils/openLink';
import { useAcceptanceData } from '../../hooks/useAcceptanceData';
import { acceptanceType } from '../../hooks/useAcceptance';
import { useFullScreenModal } from '../../hooks/useFullScreenModal';
import config from '../../config';

const CURRENCY_CONFIG = {
  BYN: {
    IconSmall: ({ className = '' }) => (
      <img src="/icons/BYN-small.svg" alt="currency" className={`w-[9px] ml-[4px] ${className}`} />
    ),
  },
  STAR: {
    IconSmall: ({ className = '' }) => (
      <img src="/icons/tg_star.svg" alt="currency" className={`w-[14px] ml-[4px] ${className}`} />
    ),
  },
};

export function Payment() {
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const [isLoading, setIsLoading] = useState(false);
  const { acceptance } = useAcceptanceData(acceptanceType.offer_for_the_provision_of_paid_services);
  const { Modal, setOpen } = useFullScreenModal();

  const [payment, setPayment] = useState('STAR');
  const [products, setProducts] = useState([]);

  useEffect(() => {
    setIsLoading(true);
    apiProducts
      .fetchAllProducts(axiosPrivate, { type: 'currency_package' })
      .then((res) => setProducts(res.data.items))
      .finally(() => setIsLoading(false));
  }, []);

  const handleByProduct = async (productId) => {
    try {
      const response =
        payment === 'BYN'
          ? await apiPayment.createOrder(axiosPrivate, productId)
          : await apiPayment.createInvoice(axiosPrivate, productId);

      if (payment === 'BYN') {
        openLink(response.redirectUrl);
      } else {
        toast.success('Оплатите заказ в боте!');
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[100vh]">
        <DotSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-start items-center w-[100%] font-raleway">
      <img
        src="/icons/Button-back.svg"
        alt="Назад"
        onClick={() => {
          navigate(-1);
        }}
        className="absolute left-4 top-4 w-[44px] h-[44px]"
      />
      <div className="flex items-center justify-center border-[#233636] border-b w-full mb-4">
        <p className="text-white text-[24px] py-[21px]">Пополнение счёта</p>
      </div>

      <div className="px-4 w-full pb-6">
        <div className="w-full flex items-center justify-between bg-[#022424] px-1.5 py-1 rounded-[400px] font-raleway border-2 border-[#233636]">
          <div
            onClick={() => config.BYN_PAYMENT_FROZEN ? toast.info(config.PURCHASES_FROZEN_MESSAGE) : setPayment('BYN')}
            className={` ${payment === 'BYN' ? 'bg-[#043939]' : 'bg-transparent'} w-1/2 h-[33px] rounded-[400px] flex justify-center items-center `}
          >
            <span className={`${payment === 'BYN' ? 'text-[#A1F69E]' : 'text-white'} text-[14px]`}>Деньги</span>
          </div>
          <div
            onClick={() => setPayment('STAR')}
            className={` ${payment === 'STAR' ? 'bg-[#043939]' : 'bg-transparent'} w-1/2 h-[33px] rounded-[400px] flex justify-center items-center`}
          >
            <span className={`${payment === 'STAR' ? 'text-[#A1F69E]' : 'text-white'} text-[14px]`}>
              Telegram звезды
            </span>
          </div>
        </div>

        <p className="font-raleway font-medium text-[13px] text-white my-4">
          Покупая монеты вы соглашаетесь с {' '}
          <span
            onClick={() => setOpen(true)}
            className="underline text-[#A1F69E] cursor-pointer pointer-events-auto text-wrap"
          >
            Офертой об оказании платных услуг
          </span>
        </p>

        <div className="flex flex-col w-full gap-2">
          {products.map((p) => (
            <PaymentCard
              key={p._id}
              id={p._id}
              payment={payment}
              amount={p.baseAmount}
              price={p.prices.find((pr) => pr.currency === payment)}
              handleBy={handleByProduct}
            />
          ))}
        </div>
      </div>
      {acceptance && (
        <Modal title={acceptance.title}>
          <div className="whitespace-pre-line">{acceptance.content}</div>
        </Modal>
      )}
    </div>
  );
}

function getPrice(priceAmount, priceDiscount) {
  const isDiscount = Boolean(priceDiscount);
  const finalPrice = priceAmount - priceDiscount;

  return {
    currentPrice: Number(priceAmount).toLocaleString('ru-RU'),
    fullPrice: Number(finalPrice).toLocaleString('ru-RU'),
    discountPercent: isDiscount ? ((finalPrice / priceAmount) * 100 - 100).toFixed(0) : 0,
  };
}

function PaymentCard({ id, amount, price, payment, handleBy }) {
  const { IconSmall } = CURRENCY_CONFIG[payment] || {};

  const { fullPrice, currentPrice, discountPercent } = getPrice(price.amount, price.discount);

  return (
    <div className="flex flex-row justify-between w-full border-b border-[#233636] pb-[12px] items-center">
      <div className="flex flex-row flex-1">
        <div className="flex flex-col">
          <span className="text-white text-[24px] flex flex-row items-center">
            <img src="/icons/coin.svg" className="w-[30px] h-[30px] pr-[8px]" />
            {amount}
          </span>
          <div className="flex flex-wrap gap-1 opacity-80">
            <span className="text-white text-[16px">за</span>
            <span className="text-white flex flex-row items-center justify-center">
              {fullPrice}
              <IconSmall />
            </span>
          </div>
        </div>
        {!!discountPercent && (
          <div className="flex flex-col justify-center items-center gap-[7px] min-w-16 ml-auto mr-6">
            <div className="w-full h-[29px] bg-[#F28A00] flex items-center justify-center rounded-[400px] text-[18px] text-white">
              {discountPercent}%
            </div>
            <span className="relative text-white text-[16px] opacity-80 text-center">
              <div className="ml-[5px] flex flex-row items-center justify-center">
                {currentPrice} <IconSmall />
              </div>
              <span className="absolute inset-0 w-full h-[2px] bg-red-500 rotate-[12deg] top-1/2 left-0"></span>
            </span>
          </div>
        )}
      </div>
      <button
        onClick={() => (config.PURCHASES_FROZEN ? toast.info(config.PURCHASES_FROZEN_MESSAGE) : handleBy(id))}
        className={`h-[48px] w-[120px] shrink-0 rounded-[400px] bg-[#A1F69E] text-black flex items-center justify-center text-[18px] disabled:opacity-50 disabled:cursor-not-allowed ${
          config.PURCHASES_FROZEN ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        Купить
      </button>
    </div>
  );
}
